import type { CostoExtra, Finanziamento, Immobile, Preventivo, Lavoro, EstimateLevel } from '../types';

/**
 * Riga del resoconto: un gruppo omogeneo di spese (una categoria di costi
 * sostenuti, oppure un lavoro con le sue voci CME).
 *
 * Il confronto stima/consuntivo è fatto **solo sulle voci effettivamente
 * pagate**: confrontare il consuntivo parziale con la stima totale darebbe uno
 * scostamento sempre negativo e privo di significato.
 */
/** Categorie di CostoExtra che rappresentano attrezzatura, non opera. */
const CATEGORIE_ATTREZZI = ['attrezzi', 'attrezzatura', 'utensili'];

export interface RigaResoconto {
  chiave: string;
  etichetta: string;
  gruppo: 'acquisto' | 'lavori' | 'attrezzi';
  /** Stima di tutte le voci attive del gruppo. */
  stimaTotale: number;
  /** Quanto è già uscito: consuntivi delle voci pagate + costi sostenuti. */
  speso: number;
  /** Stima delle voci non ancora pagate. */
  daSpendere: number;
  /** Consuntivo meno stima, sulle sole voci pagate. Null se nulla è ancora pagato. */
  scostamento: number | null;
  /** Scostamento in percentuale sulla stima delle voci pagate. Null se non calcolabile. */
  scostamentoPerc: number | null;
  vociPagate: number;
  vociTotali: number;
}

export interface Resoconto {
  righe: RigaResoconto[];
  totali: {
    speso: number;
    daSpendere: number;
    stimaTotale: number;
    scostamento: number | null;
    /**
     * Quota di `speso` che è attrezzatura e non opera. Sono soldi realmente
     * usciti, quindi restano nei totali, ma vanno letti a parte: un tassellatore
     * resta tuo a fine cantiere, una tubazione no.
     */
    attrezzi: number;
  };
  /** Prezzo dell'immobile: tenuto fuori dai totali perché coperto dal mutuo. */
  prezzoImmobile: number;
  copertura: {
    totale: number;
    mutui: number;
    prestiti: number;
    fondiPropri: number;
    /** Copertura meno (prezzo + speso + daSpendere). Negativo = scoperto. */
    saldo: number;
  };
}

function num(value: string | number | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

/** Stima di una voce CME al livello richiesto, con fallback su importoStimato. */
export function stimaPreventivo(p: Preventivo, level: EstimateLevel = 'med'): number {
  const perLivello = level === 'min' ? p.costoMin : level === 'max' ? p.costoMax : p.costoMed;
  const stima = num(perLivello);
  if (stima > 0) return stima;
  return num(p.importoStimato);
}

/** Una voce è "pagata" quando ha un consuntivo valorizzato. */
export function isPagata(p: Preventivo): boolean {
  return p.importoReale !== null && p.importoReale !== undefined && num(p.importoReale) > 0;
}

export interface CalcolaResocontoInput {
  preventivi: Preventivo[];
  costiExtra: CostoExtra[];
  finanziamenti: Finanziamento[];
  lavori?: Lavoro[];
  immobile?: Immobile | null;
  estimateLevel?: EstimateLevel;
}

export function calcolaResoconto({
  preventivi,
  costiExtra,
  finanziamenti,
  lavori = [],
  immobile = null,
  estimateLevel = 'med',
}: CalcolaResocontoInput): Resoconto {
  const righe: RigaResoconto[] = [];

  // --- Spese di acquisto: i costi extra, raggruppati per categoria ---
  const perCategoria = new Map<string, CostoExtra[]>();
  for (const c of costiExtra) {
    const key = c.categoria?.trim() || 'altro';
    const list = perCategoria.get(key);
    if (list) list.push(c);
    else perCategoria.set(key, [c]);
  }

  for (const [categoria, costi] of perCategoria) {
    const speso = costi.reduce((s, c) => s + num(c.importo), 0);
    const isAttrezzo = CATEGORIE_ATTREZZI.includes(categoria.toLowerCase());
    righe.push({
      chiave: `${isAttrezzo ? 'attrezzi' : 'acquisto'}:${categoria}`,
      etichetta: categoria,
      gruppo: isAttrezzo ? 'attrezzi' : 'acquisto',
      // Un costo sostenuto non ha una stima a cui confrontarsi: è già uscito.
      stimaTotale: speso,
      speso,
      daSpendere: 0,
      scostamento: null,
      scostamentoPerc: null,
      vociPagate: costi.length,
      vociTotali: costi.length,
    });
  }

  // --- Lavori: voci CME attive, raggruppate per lavoro (fallback: categoria) ---
  const nomeLavoro = new Map(lavori.map((l) => [l.id, l.nome]));
  const perGruppo = new Map<string, Preventivo[]>();

  for (const p of preventivi) {
    // Coerente con la pagina Preventivi: le voci disattivate non contano.
    if (p.attivo === false) continue;
    const key = p.lavoroId ? `lavoro:${p.lavoroId}` : `categoria:${p.categoria}`;
    const list = perGruppo.get(key);
    if (list) list.push(p);
    else perGruppo.set(key, [p]);
  }

  for (const [key, voci] of perGruppo) {
    const etichetta = key.startsWith('lavoro:')
      ? nomeLavoro.get(key.slice('lavoro:'.length)) ?? 'Lavoro'
      : key.slice('categoria:'.length);

    const pagate = voci.filter(isPagata);
    const nonPagate = voci.filter((p) => !isPagata(p));

    const stimaTotale = voci.reduce((s, p) => s + stimaPreventivo(p, estimateLevel), 0);
    const speso = pagate.reduce((s, p) => s + num(p.importoReale), 0);
    const daSpendere = nonPagate.reduce((s, p) => s + stimaPreventivo(p, estimateLevel), 0);
    const stimaPagate = pagate.reduce((s, p) => s + stimaPreventivo(p, estimateLevel), 0);

    const scostamento = pagate.length > 0 ? speso - stimaPagate : null;
    const scostamentoPerc =
      pagate.length > 0 && stimaPagate > 0 ? ((speso - stimaPagate) / stimaPagate) * 100 : null;

    righe.push({
      chiave: key,
      etichetta,
      gruppo: 'lavori',
      stimaTotale,
      speso,
      daSpendere,
      scostamento,
      scostamentoPerc,
      vociPagate: pagate.length,
      vociTotali: voci.length,
    });
  }

  const ordineGruppo = { acquisto: 0, lavori: 1, attrezzi: 2 };
  righe.sort((a, b) => {
    if (a.gruppo !== b.gruppo) return ordineGruppo[a.gruppo] - ordineGruppo[b.gruppo];
    return b.stimaTotale - a.stimaTotale;
  });

  const speso = righe.reduce((s, r) => s + r.speso, 0);
  const attrezzi = righe
    .filter((r) => r.gruppo === 'attrezzi')
    .reduce((s, r) => s + r.speso, 0);
  const daSpendere = righe.reduce((s, r) => s + r.daSpendere, 0);
  const stimaTotale = righe.reduce((s, r) => s + r.stimaTotale, 0);

  const conScostamento = righe.filter((r) => r.scostamento !== null);
  const scostamento =
    conScostamento.length > 0 ? conScostamento.reduce((s, r) => s + (r.scostamento ?? 0), 0) : null;

  // --- Copertura finanziaria ---
  const mutui = finanziamenti
    .filter((f) => f.tipo === 'mutuo')
    .reduce((s, f) => s + num(f.importo), 0);
  const prestiti = finanziamenti
    .filter((f) => f.tipo === 'prestito')
    .reduce((s, f) => s + num(f.importo), 0);
  const fondiPropri = finanziamenti
    .filter((f) => f.tipo === 'fondi_propri')
    .reduce((s, f) => s + num(f.importo), 0);
  const coperturaTotale = mutui + prestiti + fondiPropri;

  const prezzoImmobile = num(immobile?.prezzoAcquisto);

  return {
    righe,
    totali: { speso, daSpendere, stimaTotale, scostamento, attrezzi },
    prezzoImmobile,
    copertura: {
      totale: coperturaTotale,
      mutui,
      prestiti,
      fondiPropri,
      saldo: coperturaTotale - (prezzoImmobile + speso + daSpendere),
    },
  };
}

/** Righe del resoconto in CSV, separatore `;` per compatibilità con Excel italiano. */
export function resocontoToCsv(resoconto: Resoconto): string {
  const header = ['Gruppo', 'Voce', 'Stima totale', 'Speso', 'Da spendere', 'Scostamento', 'Voci pagate'];
  const righe = resoconto.righe.map((r) => [
    r.gruppo,
    r.etichetta,
    r.stimaTotale.toFixed(2),
    r.speso.toFixed(2),
    r.daSpendere.toFixed(2),
    r.scostamento !== null ? r.scostamento.toFixed(2) : '',
    `${r.vociPagate}/${r.vociTotali}`,
  ]);
  const totale = [
    'TOTALE',
    '',
    resoconto.totali.stimaTotale.toFixed(2),
    resoconto.totali.speso.toFixed(2),
    resoconto.totali.daSpendere.toFixed(2),
    resoconto.totali.scostamento !== null ? resoconto.totali.scostamento.toFixed(2) : '',
    '',
  ];
  return [header, ...righe, totale].map((r) => r.join(';')).join('\n');
}
